var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, computed, makeObservable } from 'mobx';
import moment from 'moment';
import _groupBy from 'lodash/groupBy';
import CarsAPI from '../services/CarsAPI';
import UserActionsAPI from '../services/UserActionsAPI';
import SyncAPI from '../services/SyncAPI';
import { CARS_LIMIT } from '../framework/constants/cars';
import { MY_VEHICLES_SORT_DEFAULT } from '../framework/constants/data';
import { formatTimestamp } from '@bytebrand/fe-ui-core-autobahn/utils';
import ImageImportAPI from '../services/ImageImportAPI';
import { ShowNotificationError } from '../framework/ErrorLogger';
import FinancingStore from '../stores/FinancingStore';
class MyVehiclesListStore {
    constructor() {
        Object.defineProperty(this, "cars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "onlineCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "aggStatsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "carExportData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "activeSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MY_VEHICLES_SORT_DEFAULT
        });
        Object.defineProperty(this, "totalCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "message", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "copyCar", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => UserActionsAPI.copyCar(carId)
        });
        Object.defineProperty(this, "copyCarForDealer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, newData) => UserActionsAPI.copyCarForDealer(carId, newData)
        });
        Object.defineProperty(this, "restoreCarForSuperAdmin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newData) => UserActionsAPI.restoreCarForSuperAdmin(newData)
        });
        Object.defineProperty(this, "removeCarFromCarList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carID) => CarsAPI.removeCarFromCarList(carID)
                .then(() => {
                const index = this._getCarIndexById(carID);
                this.cars.delete(index);
            })
        });
        Object.defineProperty(this, "changeCarSellingStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carID) => {
                const index = this._getCarIndexById(carID);
                const car = this.cars.get(index);
                const updatedCar = {
                    _id: carID,
                    state: {
                        isOnline: !car.state.isOnline,
                        // in case if firstIsOnline is not yet set generate a fresh timestamp instead of pulling the whole car state
                        firstIsOnline: car.state.firstIsOnline || moment.utc().unix()
                    }
                };
                return CarsAPI.updateCarData(carID, { data: updatedCar })
                    .then(() => {
                    car.state.isOnline = !car.state.isOnline;
                    car.state.firstIsOnline = car.state.firstIsOnline || moment.utc().unix();
                });
            }
        });
        Object.defineProperty(this, "getMyCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page = 1, search, imagesByApp, forceUpdate) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                if (this.cars.has(offset) && !forceUpdate) {
                    const cars = this.getCarsForPage(page);
                    return Promise.resolve(cars);
                }
                this.isFetching = true;
                return CarsAPI.getMyCarsList(offset, CARS_LIMIT, this.activeSorting, search, imagesByApp)
                    .then((response) => {
                    const { data: cars, pagination } = response;
                    this.totalCars = pagination.total;
                    // this.cars.set(offset, cars);
                    cars.forEach((vehicle, i) => {
                        const transformedVehicle = FinancingStore.normalizeRateObject(vehicle);
                        this.cars.set(offset + i, transformedVehicle);
                    });
                    this.isFetching = false;
                    return this.getCarsForPage(page);
                }).catch((error) => {
                    this.isFetching = false;
                    ShowNotificationError('errors.loadMyCarsList');
                    return [];
                });
            }
        });
        Object.defineProperty(this, "getCarsForPage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                const cars = [];
                for (let i = offset; i < (offset + CARS_LIMIT); i += 1) {
                    if (this.cars.has(i))
                        cars.push(this.cars.get(i));
                }
                return cars;
            }
        });
        Object.defineProperty(this, "getAggStatsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carsIds) => {
                if (this.aggStatsData.size !== 0)
                    return;
                return UserActionsAPI.getStatsData(carsIds)
                    .then((response) => {
                    const aggStatsData = {};
                    response.data.forEach((data) => {
                        aggStatsData[data.carId] = data;
                    });
                    this.aggStatsData.merge(aggStatsData);
                    return response.data;
                })
                    .catch((err) => {
                    ShowNotificationError('errors.loadStatistic');
                });
            }
        });
        Object.defineProperty(this, "getExportData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carIds, force) => {
                const requestArray = carIds.filter((carId) => {
                    return !this.carExportData.has(carId);
                });
                if (requestArray.length > 0 || force) {
                    return SyncAPI.getSyncStatus(force ? carIds : requestArray)
                        .then((response) => {
                        response.data.forEach((dataObj) => {
                            this.carExportData.set(dataObj.autoid, dataObj);
                        });
                        return response;
                    })
                        .catch((error) => {
                        ShowNotificationError('errors.loadExportData');
                    });
                }
            }
        });
        Object.defineProperty(this, "syncCarToPlatform", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                this.carExportData.set(carId, {
                    autoid: carId,
                    exportInfo: {},
                    exportStatus: 'inprogress'
                });
                return SyncAPI.syncCarToPlatform(carId)
                    .catch((error) => {
                    const exportData = this.carExportData.get(carId);
                    this.carExportData.set(carId, { ...exportData, exportStatus: 'error' });
                    return Promise.reject(error);
                });
            }
        });
        Object.defineProperty(this, "changeSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (sorting) => {
                this.activeSorting = sorting;
            }
        });
        Object.defineProperty(this, "clearCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.cars.clear();
                this.aggStatsData.clear();
                this.onlineCars.clear();
            }
        });
        Object.defineProperty(this, "clearImageCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID) => {
                ImageImportAPI.clearImageCache(vehicleID);
            }
        });
        Object.defineProperty(this, "_getCarIndexById", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carID) => {
                for (const [index, car] of this.cars.entries()) {
                    if (car._id === carID)
                        return index;
                }
            }
        });
        makeObservable(this);
    }
    get onlineCarsLength() {
        let onlineCarsCount = 0;
        this.onlineCars.forEach((value) => {
            onlineCarsCount += value.length;
        });
        return onlineCarsCount;
    }
    getOnlineCars(startTime, endTime) {
        this.onlineCars.clear();
        return CarsAPI.getOnlineCars(startTime, endTime)
            .then((response) => {
            const groupedByMonth = _groupBy(response, (el) => formatTimestamp({
                timestamp: el.state.firstIsOnline,
                format: 'DD.MM.YYYY',
                parseFormat: 'X'
            }));
            this.onlineCars.replace(groupedByMonth);
        });
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "cars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "onlineCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "aggStatsData", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "carExportData", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "activeSorting", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "totalCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "message", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "isFetching", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], MyVehiclesListStore.prototype, "onlineCarsLength", null);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "copyCar", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "copyCarForDealer", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "restoreCarForSuperAdmin", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "removeCarFromCarList", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "changeCarSellingStatus", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "getMyCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "getCarsForPage", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "getAggStatsData", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Number]),
    __metadata("design:returntype", void 0)
], MyVehiclesListStore.prototype, "getOnlineCars", null);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "getExportData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "syncCarToPlatform", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "changeSorting", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "clearCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], MyVehiclesListStore.prototype, "clearImageCache", void 0);
export default new MyVehiclesListStore();
